import React, { FC, useContext } from "react";
import { cloneDeep } from "lodash";
import { NetworkComparisonFiltersContext, NetworkComparisonFiltersContextProvider } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters";
import { BenchmarkingFiltersContext, BenchmarkingFiltersContextProvider } from "@app/overviews/network-benchmarking-dashboard/filters/BenchmarkingFiltersContext";
import Page from "@app/shared/Page";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import BenchmarkingContextProvider, { BenchmarkingContext } from "@app/overviews/network-benchmarking-dashboard/BenchmarkingContext";
import CompetitiveInsightsDefaultView from "@app/overviews/network-benchmarking-dashboard/CompetitiveInsightsDefaultView";
import BenchmarkingDashboardDetailsView from "@app/overviews/network-benchmarking-dashboard/dashboard-details/BenchmarkingDashboardDetailsView";
import { NetworkComparisonFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/shared/NetworkComparisonFiltersPayload";
import { BenchmarkingLineOfBusinessEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared";
import QHPFiltersContextProvider from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/QHPFiltersContext";
import MedicareFiltersContextProvider from "@app/overviews/network-benchmarking-dashboard/medicare/filters/MedicareFiltersContext";
import MedicaidFiltersContextProvider from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/MedicaidFiltersContext";

export const WrappedBenchmarkingDashboard: React.FC<React.PropsWithChildren<unknown>> = () => {
  const benchmarkingContext = useContext(BenchmarkingContext);
  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);
  const networkComparisonFiltersContext = useContext(NetworkComparisonFiltersContext);

  const onBackToFullViewClick = (): void => {
    // in case the user toggled filters without hitting apply, proceeded to click into the dashboard details view, and then want to go back to the dashboard,
    // reset all filters to reflect the actual data (not what was toggled and not actually applied)
    const originalFilters: NetworkComparisonFiltersPayload = cloneDeep(networkComparisonFiltersContext.originalDisplayedNetworkComparisonFilters);
    networkComparisonFiltersContext.setDisplayedNetworkComparisonFilters(originalFilters);
    networkComparisonFiltersContext.setSelectedNetwork(originalFilters.primaryNetwork);
    networkComparisonFiltersContext.setSelectedSpecialties(benchmarkingFiltersContext.rawSpecialties.map(x => x.specialtyGroupId.toString()).filter(x => originalFilters.specialtyGroupIds.map(y => y.toString()).includes(x)));
    networkComparisonFiltersContext.setSelectedPcaLineOfBusiness(originalFilters.pcaLineOfBusiness);
    networkComparisonFiltersContext.setSelectedVolumeIndicators(cloneDeep(networkComparisonFiltersContext.mappedVolumeIndicators).filter(x => originalFilters.volumeIndicators.includes(x.key)));
    networkComparisonFiltersContext.setSelectedEffectivenessValues(cloneDeep(networkComparisonFiltersContext.mappedEffectivenessAndEfficiencyValues).filter(x => originalFilters.effectivenessRanks.includes(x.key)));
    networkComparisonFiltersContext.setSelectedEfficiencyValues(cloneDeep(networkComparisonFiltersContext.mappedEffectivenessAndEfficiencyValues).filter(x => originalFilters.efficiencyRanks.includes(x.key)));
    benchmarkingContext.setShowDashboardDetails(false);
  }

  const getFiltersContextByLineOfBusiness = () => {
    const benchmarkingChildren = (
      <PageSection>
        {benchmarkingContext.showDashboardDetails ? (
          <BenchmarkingDashboardDetailsView
            filtersPayload={networkComparisonFiltersContext.displayedNetworkComparisonFilters}
            onBackToFullViewClick={onBackToFullViewClick}
            totalCount={benchmarkingContext.dashboardDetailsTotalCount}
          />
        ) : (
          <CompetitiveInsightsDefaultView />
        )}
      </PageSection>
  );

    switch (benchmarkingFiltersContext.selectedLineOfBusiness) {
      case BenchmarkingLineOfBusinessEnum.Medicare:
        return (
          <MedicareFiltersContextProvider>
            {benchmarkingChildren}
          </MedicareFiltersContextProvider>
        );
      case BenchmarkingLineOfBusinessEnum.Marketplace:
        return (
          <QHPFiltersContextProvider>
            {benchmarkingChildren}
          </QHPFiltersContextProvider>
        );
      case BenchmarkingLineOfBusinessEnum.Medicaid:
        return (
          <MedicaidFiltersContextProvider>
            {benchmarkingChildren}
          </MedicaidFiltersContextProvider>
        );
      default:
        return (
          <MedicareFiltersContextProvider>
            {benchmarkingChildren}
          </MedicareFiltersContextProvider>
        );
    }
  } 

  return (
    <Page className="bg-white benchmarking-dashboard__page" documentTitle={`Competitive Insights Dashboard${benchmarkingContext.showDashboardDetails && " Details"}`}>
      <PageInner>
        {getFiltersContextByLineOfBusiness()}
      </PageInner>
    </Page>
  );
}

const BenchmarkingDashboard: FC = () => (
  <BenchmarkingFiltersContextProvider>
    <NetworkComparisonFiltersContextProvider>
      <BenchmarkingContextProvider>
        <WrappedBenchmarkingDashboard />
      </BenchmarkingContextProvider>
    </NetworkComparisonFiltersContextProvider>
  </BenchmarkingFiltersContextProvider>
);

export default BenchmarkingDashboard;
